import type { ReactElement } from 'react';

import { Flex, Tabs } from '@rango-dev/whale';
import { t } from 'i18next';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { dispatchCancellableAction, fetchProfileLeaderboard } from '../../../state/ProfileSlice';
import { Campaigns } from '../../CampaignsSidebar/Campaigns';
import { Leaderboard } from '../Leaderboard';
import { ProfileUserData } from '../ProfileUserData';

import styles from './ProfileMobileMode.module.css';
import { ProfileSummaryMobile } from './ProfileSummaryMobile';

const SUMMARY_TAB = 'summary';
const STATS_TAB = 'stats';
const LEADERBOARD_TAB = 'leaderboard';

export function ProfileMobileMode(): ReactElement {
  const { isLoggedIn, username } = useAppSelector((state) => state.profile);
  const params = useParams<{ username: string }>();
  const showPrivateProfile = isLoggedIn && username === params.username;

  const [selectedTab, setSelectedTab] = useState<string>(SUMMARY_TAB);
  const {
    data: profileLeaderboard,
    loading: profileLeaderboardLoading,
    error: profileLeaderboardError,
  } = useAppSelector((state) => state.profile.profileLeaderboard);

  const dispatch = useAppDispatch();
  const refetchProfileLeaderboard = () => {
    if (params.username) {
      dispatchCancellableAction(dispatch, fetchProfileLeaderboard, { username: params.username });
    }
  };

  const renderContent = () => {
    switch (selectedTab) {
      case SUMMARY_TAB:
        return <ProfileSummaryMobile />;
      case STATS_TAB:
        return <ProfileUserData />;
      case LEADERBOARD_TAB:
        return (
          <Leaderboard
            list={profileLeaderboard}
            loading={profileLeaderboardLoading}
            error={profileLeaderboardError}
            onRetry={refetchProfileLeaderboard}
          />
        );
      default:
        return <ProfileSummaryMobile />;
    }
  };

  return (
    <Flex direction="column" gap="4" width="100%">
      <Tabs.Root value={selectedTab} onValueChange={setSelectedTab}>
        <Tabs.List size="2">
          <Tabs.Trigger value={SUMMARY_TAB} className={styles.tabTrigger}>
            {t('Summary')}
          </Tabs.Trigger>
          {showPrivateProfile && (
            <Tabs.Trigger value={STATS_TAB} className={styles.tabTrigger}>
              {t('Stats')}
            </Tabs.Trigger>
          )}
          <Tabs.Trigger value={LEADERBOARD_TAB} className={styles.tabTrigger}>
            {t('Leaderboard')}
          </Tabs.Trigger>
        </Tabs.List>
      </Tabs.Root>
      {renderContent()}
      {selectedTab !== STATS_TAB && <Campaigns />}
    </Flex>
  );
}
